/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
  Dimensions,
  TextStyle,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import CrossPlatformDropdown from "../../../../components/CrossPlatformDropdown";
import LhButton from "../../../../components/LhButton";
import LhDatePicker from "../../../../components/LhDatePicker";
import { useEmployeeForm } from "../../hooks/useEmployeeForm";
import {
  createEmployee,
  clearValidationErrors,
  setValidationError,
  resetEmployeeDates,
  lookupEmployeeByGpid,
  resetEmployeeData,
} from "../../../../store/slices/employeeUpdatesSlice";
import { AppDispatch } from "../../../../store";
import FontFamily from "../../../../constants/fonts";
import { useTheme } from "../../../../theme/useTheme";

interface EmployeeFormSectionProps {
  statusOptions: Array<{ id: string; displayText: string }>;
  managerOptions: Array<{ id: string; displayText: string }>;
  onAddEmployee: () => void;
  onCancel?: () => void;
  hireDate: Date | null;
  setHireDate: (date: Date | null) => void;
  graduationDate: Date | null;
  setGraduationDate: (date: Date | null) => void;
  onResetForm?: React.MutableRefObject<(() => void) | null>;
}

const EmployeeFormSection: React.FC<EmployeeFormSectionProps> = ({
  statusOptions = [],
  managerOptions = [
    { id: "1", displayText: "Alice Johnson" },
    { id: "2", displayText: "Bob Smith" },
    { id: "3", displayText: "Carol Lee" },
    { id: "4", displayText: "David Kim" },
  ],
  onAddEmployee,
  onCancel,
  hireDate,
  setHireDate,
  graduationDate,
  setGraduationDate,
  onResetForm,

  // onSelectHireDate,
  // onSelectGraduationDate,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const dispatch = useDispatch<AppDispatch>();
  const { formState, handlers, validationErrors } = useEmployeeForm();

  // Add state for notification messages
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Get roles from Redux store
  const { roles: appDataRoles } = useSelector((state: any) => state.appData);
  
  // Get GPID lookup state from Redux store
  const { gpidLookupLoading, gpidLookupError, isNameFromGpidLookup } = useSelector((state: any) => state.employeeUpdates);

  // Create job description dropdown options from roles data
  const jobDescriptionOptions = useMemo(() => {
    if (!appDataRoles || !Array.isArray(appDataRoles)) {
      console.log("No roles data found in appDataRoles:", appDataRoles);
      return [];
    }

    const roleOptions = appDataRoles
      .filter((role: any) => role.ROLE_NAME && role.ROLE_ID)
      .map((role: any) => ({
        id: role.ROLE_ID.toString(),
        displayText: role.ROLE_NAME,
      }))
      .sort((a: any, b: any) => a.displayText.localeCompare(b.displayText));

    return roleOptions;
  }, [appDataRoles]);

  // Add loading state to track API call status
  const [isLoading, setIsLoading] = useState(false);

  // Destructure form state and handlers for easier access
  const { firstName, lastName, gpid, manager, jobDescription, selectedStatus } =
    formState;

  const {
    handleFirstNameChange,
    handleLastNameChange,
    handleGpidChange,
    handleManagerChange,
    handleJobDescriptionChange,
    handleStatusChange,
    handleClearValidationError,
  } = handlers;

  // Create local state for responsive input handling
  const [localState, setLocalState] = useState({
    firstName: firstName || "",
    lastName: lastName || "",
    gpid: gpid || "",
    manager: manager || "",
    jobDescription: jobDescription || "",
  });

  // Create refs for debouncing
  const debounceTimers = useRef<{
    [key: string]: ReturnType<typeof setTimeout>;
  }>({}); // Better type for timers

  // Debounce function to update Redux state
  const debounceUpdate = (field: string, value: string) => {
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }

    debounceTimers.current[field] = setTimeout(() => {
      // Update Redux store after a delay
      switch (field) {
        case "firstName":
          handleFirstNameChange(value);
          break;
        case "lastName":
          handleLastNameChange(value);
          break;
        case "gpid":
          handleGpidChange(value);
          break;
        case "manager":
          handleManagerChange(value);
          break;
        case "jobDescription":
          handleJobDescriptionChange(value);
          break;
        default:
          break;
      }
    }, 300); // 300ms debounce delay
  };

  // Reset function to clear all form values
  const resetForm = () => {
    // Clear all debounce timers
    Object.keys(debounceTimers.current).forEach((key) => {
      clearTimeout(debounceTimers.current[key]);
    });

    // Reset local state
    setLocalState({
      firstName: "",
      lastName: "",
      gpid: "",
      manager: "",
      jobDescription: "",
    });

    // Reset Redux store fields (this will also reset isNameFromGpidLookup flag)
    dispatch(resetEmployeeData());

    // Reset status
    handleStatusChange(null);

    // Reset dates (these are controlled by parent)
    setHireDate(null);
    setGraduationDate(null);

    // Clear validation errors
    dispatch(clearValidationErrors());

    // Clear notification messages
    setSuccessMessage("");
    setErrorMessage("");

    // Reset loading state
    setIsLoading(false);
  };

  // Expose reset function to parent component via onResetForm prop
  useEffect(() => {
    if (onResetForm) {
      onResetForm.current = resetForm;
    }
  }, [onResetForm]);

  // GPID validation and formatting function
  const validateAndFormatGpid = (value: string): string => {
    // Remove any non-numeric characters (spaces, special characters, letters)
    const cleaned = value.replace(/[^0-9]/g, "");

    // Limit to 8 digits maximum
    const limited = cleaned.substring(0, 8);

    // Log for debugging if input was modified
    if (value !== limited && value.length > 0) {
      console.log(`GPID input cleaned: "${value}" -> "${limited}"`);
    }

    return limited;
  };

  // Safe GPID lookup function with error boundary
  const performGpidLookup = async (gpid: string) => {
    try {
      console.log("Triggering GPID lookup for:", gpid);
      
      // Dispatch the lookup action
      const result = await dispatch(lookupEmployeeByGpid(gpid));
      
      // Check if it was successful
      if (lookupEmployeeByGpid.fulfilled.match(result)) {
        console.log("GPID lookup successful:", result.payload);
      } else if (lookupEmployeeByGpid.rejected.match(result)) {
        console.log("GPID lookup failed (handled):", result.payload);
        // Error is already handled in the reducer
      }
      
      return result;
    } catch (error) {
      console.log("GPID lookup error caught:", error);
      // Don't let GPID lookup errors crash the app or trigger redirects
      return null;
    }
  };

  // Local update handlers for responsive updates
  const handleLocalChange = (field: string, value: string) => {
    // Prevent changes to first name and last name if they came from GPID lookup
    if ((field === "firstName" || field === "lastName") && isNameFromGpidLookup) {
      return; // Block the change
    }

    let processedValue = value;

    // Special handling for GPID field
    if (field === "gpid") {
      processedValue = validateAndFormatGpid(value);
      
      // If names were from GPID lookup and GPID is being changed, clear the names
      if (isNameFromGpidLookup && processedValue !== localState.gpid) {
        setLocalState((prev) => ({ 
          ...prev, 
          [field]: processedValue,
          firstName: "",
          lastName: ""
        }));
      } else {
        // Update local state immediately for responsive UI
        setLocalState((prev) => ({ ...prev, [field]: processedValue }));
      }
      
      // Trigger GPID lookup when GPID reaches exactly 8 digits
      if (processedValue.length === 8 && /^\d{8}$/.test(processedValue)) {
        // Use the safe lookup function to prevent app crashes
        performGpidLookup(processedValue);
      }
    } else {
      // Update local state immediately for responsive UI
      setLocalState((prev) => ({ ...prev, [field]: processedValue }));
    }

    // Only clear validation errors if there's actually some input
    // This prevents removing error messages when user just adds spaces
    if (validationErrors?.[field] && processedValue.trim()) {
      handleClearValidationError(field);
    }

    // Debounce the Redux update
    debounceUpdate(field, processedValue);
  };

  // Sync local state with Redux state when Redux changes (e.g., after form reset)
  useEffect(() => {
    setLocalState({
      firstName: firstName || "",
      lastName: lastName || "",
      gpid: gpid || "",
      manager: manager || "",
      jobDescription: jobDescription || "",
    });
  }, [firstName, lastName, gpid, manager, jobDescription]);

  // Clean up debounce timers on unmount
  useEffect(() => {
    return () => {
      // Clear all debounce timers when component unmounts
      Object.keys(debounceTimers.current).forEach((key) => {
        clearTimeout(debounceTimers.current[key]);
      });
    };
  }, []);

  // Check if device should use mobile layout (iPhone only, not iPad)
  const isMobile = Dimensions.get("window").width < 768;

  // Helper function to check if device should use web-style layout (web or iPad)
  const isWebOrIPad = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "web" || (Platform.OS === "ios" && windowWidth >= 768);
  };

  // Handle adding an employee with validation
  const handleAddEmployee = async () => {
    // Clear any previous messages
    setSuccessMessage("");
    setErrorMessage("");

    // Prevent multiple submissions
    if (isLoading) {
      return;
    }

    // Validate form and create employee if valid
    const isValid = validateForm();

    if (isValid) {
      try {
        // Format dates as date-only (YYYY-MM-DD) using local time to avoid UTC day-shift
        const toLocalDateString = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const formattedHireDate = hireDate ? toLocalDateString(hireDate) : null;
        const formattedGraduationDate = graduationDate
          ? toLocalDateString(graduationDate)
          : null;

        setIsLoading(true); // Set loading state to true

        // Dispatch action to create employee with the dates
        const result = await dispatch(
          createEmployee({
            Hire_Date: formattedHireDate,
            Expected_Graduation_Date: formattedGraduationDate,
          })
        ).unwrap();

        // Extract success message from API response
        let successMessage = "Employee added successfully";

        // Check if we have the expected response format
        if (result && typeof result === "object") {
          // Use the message from API response if available
          if (result.message) {
            successMessage = result.message;
          }

          // Log the newly created employee details for debugging
          if (result.newEmployee) {
            console.log("New Employee Created:", result.newEmployee);

            // Create a more informative success message with employee details
            const employeeName = `${result.newEmployee.First_Name} ${result.newEmployee.Last_Name}`;
            successMessage = `${
              result.message || "Employee created successfully."
            }\nEmployee: ${employeeName} (${result.newEmployee.GPID})`;
          }
        }

        // Clear any previous error message
        setErrorMessage("");

        // Reset form fields after successful submission using the resetForm function
        resetForm();

        // Immediately call the parent's onAddEmployee callback to close the overlay
        // and refresh the employee list on the main screen
        if (onAddEmployee) {
          onAddEmployee();
        }

        // Reset loading state after successful submission
        setIsLoading(false);
      } catch (error) {
        // Reset loading state on error
        setIsLoading(false);

        console.error("Error response:", error); // Log the entire error for debugging

        // Handle the error based on its format
        let errorMessage = "Failed to add employee";
        if (typeof error === "string") {
          // Handle string error (from Redux rejection)
          errorMessage = error;
        } else if (error instanceof Error) {
          // Handle standard Error objects
          errorMessage = error.message;
        } else if (error && typeof error === "object") {
          // Handle API error format: {"message":"Error creating employee"}
          if ("message" in error) {
            errorMessage = String(error.message);
          }
          // Check if the error is nested in a data property (common in axios/fetch errors)
          else if (
            "data" in error &&
            typeof error.data === "object" &&
            error.data &&
            "message" in error.data
          ) {
            errorMessage = String(error.data.message);
          }
          // Handle response errors with nested structure
          else if (
            "response" in error &&
            typeof error.response === "object" &&
            error.response &&
            "data" in error.response
          ) {
            const responseData = error.response.data;
            if (
              responseData &&
              typeof responseData === "object" &&
              "message" in responseData
            ) {
              errorMessage = String(responseData.message);
            }
          }
        }

        // Show error message in notification bar
        setErrorMessage(errorMessage);
        setSuccessMessage(""); // Clear any previous success message

        // Auto-clear error message after 5 seconds
        setTimeout(() => {
          setErrorMessage("");
        }, 5000);
      } finally {
        setIsLoading(false); // Reset loading state
      }
    }
  };

  // Internal validation function - using local state for validation
  const validateForm = () => {
    // Clear all previous validation errors
    dispatch(clearValidationErrors());

    let hasErrors = false;

    // Sync local state to Redux before validation to ensure they match
    handleFirstNameChange(localState.firstName);
    handleLastNameChange(localState.lastName);
    handleGpidChange(localState.gpid);
    handleManagerChange(localState.manager);
    handleJobDescriptionChange(localState.jobDescription);

    // Validate First Name
    if (!localState.firstName || !localState.firstName.trim()) {
      dispatch(
        setValidationError({
          field: "firstName",
          message: "First Name is required",
        })
      );
      hasErrors = true;
    }

    // Validate Last Name
    if (!localState.lastName || !localState.lastName.trim()) {
      dispatch(
        setValidationError({
          field: "lastName",
          message: "Last Name is required",
        })
      );
      hasErrors = true;
    }

    // Validate GPID
    if (!localState.gpid || !localState.gpid.trim()) {
      dispatch(
        setValidationError({ field: "gpid", message: "GPID is required" })
      );
      hasErrors = true;
    } else if (localState.gpid.length !== 8) {
      dispatch(
        setValidationError({
          field: "gpid",
          message: "GPID must be exactly 8 digits",
        })
      );
      hasErrors = true;
    } else if (!/^\d{8}$/.test(localState.gpid)) {
      dispatch(
        setValidationError({
          field: "gpid",
          message: "GPID must contain only numbers",
        })
      );
      hasErrors = true;
    }

    // Validate Status
    if (!selectedStatus) {
      dispatch(
        setValidationError({
          field: "selectedStatus",
          message: "Status is required",
        })
      );
      hasErrors = true;
    }

    // Validate Job Description
    if (!localState.jobDescription || !localState.jobDescription.trim()) {
      dispatch(
        setValidationError({
          field: "jobDescription",
          message: "Job Description is required",
        })
      );
      hasErrors = true;
    }

    // Validate Manager
    if (!localState.manager || !localState.manager.trim()) {
      dispatch(
        setValidationError({ field: "manager", message: "Manager is required" })
      );
      hasErrors = true;
    }

    // Validate Hire Date - now mandatory
    if (!hireDate) {
      dispatch(
        setValidationError({
          field: "hireDate",
          message: "Hire Date is required",
        })
      );
      hasErrors = true;
    }

    // Validate Graduation Date - now mandatory
    if (!graduationDate) {
      dispatch(
        setValidationError({
          field: "graduationDate",
          message: "Graduation Date is required",
        })
      );
      hasErrors = true;
    }

    return !hasErrors;
  };

  // Use React.useMemo to prevent unnecessary re-renders of form fields
  const renderFormFields = React.useMemo(
    () => () => {
      const darkInputStyle = isDark
        ? { backgroundColor: colors.input.background, borderColor: colors.input.border, color: colors.text.primary }
        : undefined;
      const darkDisabledInputStyle = isDark
        ? { backgroundColor: colors.background.elevated, opacity: 0.7 }
        : undefined;
      const darkLabelStyle = isDark ? { color: colors.text.secondary } : undefined;
      const darkPlaceholderColor = isDark ? colors.text.disabled : "#6C757D";

      if (isMobile) {
        return (
          <>
            {/* Row 1: GPID & First Name */}
            <View style={styles.row}>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>GPID *</Text>
                <View style={styles.gpidInputContainer}>
                  <TextInput
                    style={[
                      styles.input,
                      validationErrors?.gpid && styles.inputError,
                      darkInputStyle,
                    ]}
                    placeholder="Enter 8-digit GPID"
                    placeholderTextColor={darkPlaceholderColor}
                    value={localState.gpid}
                    onChangeText={(value) => handleLocalChange("gpid", value)}
                    keyboardType="numeric"
                    inputMode="numeric"
                    maxLength={8}
                    autoComplete="off"
                    {...(Platform.OS === "web" && {
                      pattern: "[0-9]*",
                      inputMode: "numeric" as any,
                    })}
                  />
                  {gpidLookupLoading && (
                    <View style={styles.gpidLoadingIndicator}>
                      <Text style={styles.gpidLoadingText}>Looking up...</Text>
                    </View>
                  )}
                </View>
                {validationErrors?.gpid && (
                  <Text style={styles.errorText}>{validationErrors.gpid}</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>
                  First Name *
                  {isNameFromGpidLookup && (
                    <Text style={styles.gpidIndicator}> (from GPID)</Text>
                  )}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    validationErrors?.firstName && styles.inputError,
                    isNameFromGpidLookup && styles.inputDisabled,
                    darkInputStyle,
                    isNameFromGpidLookup ? darkDisabledInputStyle : undefined,
                  ]}
                  placeholder="Enter First Name"
                  placeholderTextColor={darkPlaceholderColor}
                  value={localState.firstName}
                  onChangeText={(value) =>
                    handleLocalChange("firstName", value)
                  }
                  editable={!isNameFromGpidLookup}
                />
                {validationErrors?.firstName && (
                  <Text style={styles.errorText}>
                    {validationErrors.firstName}
                  </Text>
                )}
              </View>
            </View>

            {/* Row 2: Last Name & Manager */}
            <View style={styles.row}>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>
                  Last Name *
                  {isNameFromGpidLookup && (
                    <Text style={styles.gpidIndicator}> (from GPID)</Text>
                  )}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    validationErrors?.lastName && styles.inputError,
                    isNameFromGpidLookup && styles.inputDisabled,
                    darkInputStyle,
                    isNameFromGpidLookup ? darkDisabledInputStyle : undefined,
                  ]}
                  placeholder="Enter Last Name"
                  placeholderTextColor={darkPlaceholderColor}
                  value={localState.lastName}
                  onChangeText={(value) => handleLocalChange("lastName", value)}
                  editable={!isNameFromGpidLookup}
                />
                {validationErrors?.lastName && (
                  <Text style={styles.errorText}>
                    {validationErrors.lastName}
                  </Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Manager *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={managerOptions}
                    placeholder="Select Manager"
                    isDarkMode={isDark}
                    selectedValue={
                      managerOptions.find(
                        (option) => option.displayText === localState.manager
                      ) || null
                    }
                    setSelectedValue={(item: any) => {
                      if (item) {
                        handleLocalChange("manager", item.displayText);
                      }
                    }}
                    style={
                      validationErrors?.manager
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.manager && (
                    <Text style={styles.errorText}>
                      {validationErrors.manager}
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* Row 3: Status & Job Description */}
            <View style={styles.row}>
              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Status *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={statusOptions}
                    placeholder="Select status"
                    selection="single"
                    isDarkMode={isDark}
                    setSelectedValue={handleStatusChange}
                    selectedValue={selectedStatus}
                    style={
                      validationErrors?.selectedStatus
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.selectedStatus && (
                    <Text style={styles.errorText}>
                      {validationErrors.selectedStatus}
                    </Text>
                  )}
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Job Description *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={jobDescriptionOptions}
                    placeholder="Select Job Description"
                    isDarkMode={isDark}
                    selectedValue={
                      jobDescriptionOptions.find(
                        (option) =>
                          option.displayText === localState.jobDescription
                      ) || null
                    }
                    setSelectedValue={(item: any) => {
                      if (item) {
                        handleLocalChange("jobDescription", item.displayText);
                      }
                    }}
                    style={
                      validationErrors?.jobDescription
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.jobDescription && (
                    <Text style={styles.errorText}>
                      {validationErrors.jobDescription}
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* Row 4: Hire Date & Graduation Date */}
            <View style={styles.row}>
              <View style={[styles.inputGroup, styles.datePickerContainer]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Hire Date *</Text>
                <View
                  style={[
                    styles.datePicker,
                    validationErrors?.hireDate ? styles.inputError : null,
                  ]}
                >
                  <LhDatePicker
                    value={hireDate}
                    onChange={(date: Date | null) => {
                      setHireDate(date);
                      if (date && validationErrors?.hireDate) {
                        handleClearValidationError("hireDate");
                      }
                    }}
                    placeholder="Select Hire Date"
                    iconColor={isDark ? "#FFFFFF" : "#333333"}
                    colorScheme={isDark ? "dark" : "light"}
                    style={isDark ? undefined : {
                      pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                      dateTextStyle: { color: "#333333" },
                      placeholderTextStyle: { color: "#999999" },
                    }}
                  />
                </View>
                {validationErrors?.hireDate && (
                  <View style={{ marginTop: 10 }}>
                    <Text style={styles.errorText}>
                      {validationErrors.hireDate}
                    </Text>
                  </View>
                )}
              </View>

              <View style={[styles.inputGroup, styles.datePickerContainer]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Graduation Date *</Text>
                <View
                  style={[
                    styles.datePicker,
                    validationErrors?.graduationDate ? styles.inputError : null,
                  ]}
                >
                  <LhDatePicker
                    value={graduationDate}
                    onChange={(date: Date | null) => {
                      setGraduationDate(date);
                      if (date && validationErrors?.graduationDate) {
                        handleClearValidationError("graduationDate");
                      }
                    }}
                    placeholder="Select Graduation Date"
                    iconColor={isDark ? "#FFFFFF" : "#333333"}
                    colorScheme={isDark ? "dark" : "light"}
                    style={isDark ? undefined : {
                      pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                      dateTextStyle: { color: "#333333" },
                      placeholderTextStyle: { color: "#999999" },
                    }}
                  />
                </View>
                {validationErrors?.graduationDate && (
                  <View style={{ marginTop: 10 }}>
                    <Text style={styles.errorText}>
                      {validationErrors.graduationDate}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </>
        );
      } else {
        return (
          <>
            {/* Row 1: GPID & First Name */}
            <View style={styles.row}>
              <View style={[styles.inputGroup, { marginRight: 12 }]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>GPID *</Text>
                <View style={styles.gpidInputContainer}>
                  <TextInput
                    style={[
                      styles.input,
                      validationErrors?.gpid && styles.inputError,
                      darkInputStyle,
                    ]}
                    placeholder="Enter 8-digit GPID"
                    placeholderTextColor={darkPlaceholderColor}
                    value={localState.gpid}
                    onChangeText={(value) => handleLocalChange("gpid", value)}
                    keyboardType="numeric"
                    inputMode="numeric"
                    maxLength={8}
                    autoComplete="off"
                    {...(Platform.OS === "web" && {
                      pattern: "[0-9]*",
                      inputMode: "numeric" as any,
                    })}
                  />
                  {gpidLookupLoading && (
                    <View style={styles.gpidLoadingIndicator}>
                      <Text style={styles.gpidLoadingText}>Looking up...</Text>
                    </View>
                  )}
                </View>
                {validationErrors?.gpid && (
                  <Text style={styles.errorText}>{validationErrors.gpid}</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>
                  First Name *
                  {isNameFromGpidLookup && (
                    <Text style={styles.gpidIndicator}> (from GPID)</Text>
                  )}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    validationErrors?.firstName && styles.inputError,
                    isNameFromGpidLookup && styles.inputDisabled,
                    darkInputStyle,
                    isNameFromGpidLookup ? darkDisabledInputStyle : undefined,
                  ]}
                  placeholder="Enter First Name"
                  placeholderTextColor={darkPlaceholderColor}
                  value={localState.firstName}
                  onChangeText={(value) =>
                    handleLocalChange("firstName", value)
                  }
                  editable={!isNameFromGpidLookup}
                />
                {validationErrors?.firstName && (
                  <Text style={styles.errorText}>
                    {validationErrors.firstName}
                  </Text>
                )}
              </View>
            </View>

            {/* Row 2: Last Name & Manager */}
            <View style={styles.row}>
              <View style={[styles.inputGroup, { marginRight: 12 }]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>
                  Last Name *
                  {isNameFromGpidLookup && (
                    <Text style={styles.gpidIndicator}> (from GPID)</Text>
                  )}
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    validationErrors?.lastName && styles.inputError,
                    isNameFromGpidLookup && styles.inputDisabled,
                    darkInputStyle,
                    isNameFromGpidLookup ? darkDisabledInputStyle : undefined,
                  ]}
                  placeholder="Enter Last Name"
                  placeholderTextColor={darkPlaceholderColor}
                  value={localState.lastName}
                  onChangeText={(value) => handleLocalChange("lastName", value)}
                  editable={!isNameFromGpidLookup}
                />
                {validationErrors?.lastName && (
                  <Text style={styles.errorText}>
                    {validationErrors.lastName}
                  </Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Manager *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={managerOptions}
                    placeholder="Select Manager"
                    isDarkMode={isDark}
                    selectedValue={
                      managerOptions.find(
                        (option) => option.displayText === localState.manager
                      ) || null
                    }
                    setSelectedValue={(item: any) => {
                      if (item) {
                        handleLocalChange("manager", item.displayText);
                      }
                    }}
                    style={
                      validationErrors?.manager
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.manager && (
                    <Text style={styles.errorText}>
                      {validationErrors.manager}
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* Row 3: Status & Job Description */}
            <View style={styles.row}>
              <View style={[styles.inputGroup, { marginRight: 12 }]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Status *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={statusOptions}
                    placeholder="Select status"
                    selection="single"
                    isDarkMode={isDark}
                    setSelectedValue={handleStatusChange}
                    selectedValue={selectedStatus}
                    style={
                      validationErrors?.selectedStatus
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.selectedStatus && (
                    <Text style={styles.errorText}>
                      {validationErrors.selectedStatus}
                    </Text>
                  )}
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Job Description *</Text>
                <View style={styles.dropdownContainer}>
                  <CrossPlatformDropdown
                    childList={jobDescriptionOptions}
                    placeholder="Select Job Description"
                    isDarkMode={isDark}
                    selectedValue={
                      jobDescriptionOptions.find(
                        (option) =>
                          option.displayText === localState.jobDescription
                      ) || null
                    }
                    setSelectedValue={(item: any) => {
                      if (item) {
                        handleLocalChange("jobDescription", item.displayText);
                      }
                    }}
                    style={
                      validationErrors?.jobDescription
                        ? { ...styles.dropdownStyle, ...styles.inputError }
                        : styles.dropdownStyle
                    }
                  />
                  {validationErrors?.jobDescription && (
                    <Text style={styles.errorText}>
                      {validationErrors.jobDescription}
                    </Text>
                  )}
                </View>
              </View>
            </View>

            {/* Row 4: Hire Date & Graduation Date */}
            <View style={styles.row}>
              <View
                style={[
                  styles.inputGroup,
                  styles.datePickerContainer,
                  { marginRight: 12 },
                ]}
              >
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Hire Date *</Text>
                <View
                  style={[
                    styles.datePicker,
                    validationErrors?.hireDate ? styles.inputError : null,
                  ]}
                >
                  <LhDatePicker
                    value={hireDate}
                    onChange={(date: Date | null) => {
                      setHireDate(date);
                      if (date && validationErrors?.hireDate) {
                        handleClearValidationError("hireDate");
                      }
                    }}
                    placeholder="Select Hire Date"
                    iconColor={isDark ? "#FFFFFF" : "#333333"}
                    colorScheme={isDark ? "dark" : "light"}
                    style={isDark ? undefined : {
                      pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                      dateTextStyle: { color: "#333333" },
                      placeholderTextStyle: { color: "#999999" },
                    }}
                  />
                </View>
                {validationErrors?.hireDate && (
                  <View style={{ marginTop: 10 }}>
                    <Text style={styles.errorText}>
                      {validationErrors.hireDate}
                    </Text>
                  </View>
                )}
              </View>

              <View style={[styles.inputGroup, styles.datePickerContainer]}>
                <Text style={[styles.sectionHeader, darkLabelStyle]}>Graduation Date *</Text>
                <View
                  style={[
                    styles.datePicker,
                    validationErrors?.graduationDate ? styles.inputError : null,
                  ]}
                >
                  <LhDatePicker
                    value={graduationDate}
                    onChange={(date: Date | null) => {
                      setGraduationDate(date);
                      if (date && validationErrors?.graduationDate) {
                        handleClearValidationError("graduationDate");
                      }
                    }}
                    placeholder="Select Graduation Date"
                    iconColor={isDark ? "#FFFFFF" : "#333333"}
                    colorScheme={isDark ? "dark" : "light"}
                    style={isDark ? undefined : {
                      pickerButtonStyle: { backgroundColor: "#ffffff", borderColor: "#ced4da" },
                      dateTextStyle: { color: "#333333" },
                      placeholderTextStyle: { color: "#999999" },
                    }}
                  />
                </View>
                {validationErrors?.graduationDate && (
                  <View style={{ marginTop: 10 }}>
                    <Text style={styles.errorText}>
                      {validationErrors.graduationDate}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </>
        );
      }
    },
    [
      isMobile,
      isDark,
      localState,
      validationErrors,
      statusOptions,
      managerOptions,
      handleStatusChange,
      handleLocalChange,
      hireDate,
      graduationDate,
      setHireDate,
      setGraduationDate,
      gpidLookupLoading,
      gpidLookupError,
    ]
  );

  return (
    <View style={styles.formSection}>
      {/* Only show error messages in the form */}
      {errorMessage && (
        <View style={styles.errorMessageContainer}>
          <MaterialIcons
            name="error"
            size={24}
            color="#d32f2f"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.errorMessageText}>{errorMessage}</Text>
        </View>
      )}

      {/* GPID Lookup Status Indicator - Above form fields on iOS only */}
      {Platform.OS === "ios" && gpidLookupLoading && (
        <View style={styles.gpidStatusContainer}>
          <MaterialIcons
            name="sync"
            size={16}
            color="#0066cc"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.gpidStatusText}>Looking up employee...</Text>
        </View>
      )}
      
      {Platform.OS === "ios" && gpidLookupError && localState.gpid && localState.gpid.length === 8 && (
        <View style={styles.gpidStatusContainer}>
          <MaterialIcons
            name="info"
            size={16}
            color="#856404"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.gpidInfoText}>
            {gpidLookupError}
          </Text>
        </View>
      )}

      {renderFormFields()}

      {/* GPID Lookup Status Indicator - Below form fields on web and Android */}
      {Platform.OS !== "ios" && gpidLookupLoading && (
        <View style={styles.gpidStatusContainer}>
          <MaterialIcons
            name="sync"
            size={16}
            color="#0066cc"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.gpidStatusText}>Looking up employee...</Text>
        </View>
      )}
      
            {Platform.OS !== "ios" && gpidLookupError && localState.gpid && localState.gpid.length === 8 && (
        <View style={styles.gpidStatusContainer}>
          <MaterialIcons
            name="info"
            size={16}
            color="#856404"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.gpidInfoText}>
            {gpidLookupError}
          </Text>
        </View>
      )}

      {validationErrors?.location && (
        <Text
          style={[styles.errorText, { textAlign: "center", marginBottom: 15 }]}
        >
          {validationErrors.location}
        </Text>
      )}

      {/* Button Container - Aligned to the right */}
      <View style={styles.buttonContainer}>
        {onCancel && (
          <LhButton
            text="Cancel"
            onPress={onCancel}
            style={
              isMobile
                ? {
                    ...styles.cancelButton,
                    ...styles.mobileButton,
                    ...(isDark
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderWidth: 1,
                          borderColor: colors.border.default,
                        }
                      : undefined),
                  }
                : styles.cancelButton
            }
            textStyle={
              isDark && isMobile
                ? { ...styles.cancelButtonText, color: colors.text.primary }
                : styles.cancelButtonText
            }
            variant="secondary"
          />
        )}
        <LhButton
          text="Add Employee"
          onPress={handleAddEmployee}
          style={
            isMobile
              ? { ...styles.addButton, ...styles.mobileButton }
              : styles.addButton
          }
          textStyle={styles.addButtonText}
          variant="primary"
          isLoading={isLoading} // Pass loading state to button
        />
      </View>
    </View>
  );
};

// Device detection constants - calculated once to avoid repetition
const getDeviceInfo = () => {
  const windowWidth = Dimensions.get("window").width;
  const isIPad = Platform.OS === "ios" && windowWidth >= 768;
  const isWeb = Platform.OS === "web";
  const isWebOrIPad = isWeb || isIPad;
  const isMobileWidth = windowWidth < 768;
  
  return { windowWidth, isIPad, isWeb, isWebOrIPad, isMobileWidth };
};

const deviceInfo = getDeviceInfo();

const styles = StyleSheet.create({
  formSection: {
    marginBottom: 0,
    overflow: "visible",
    zIndex: 20000,
    position: "relative",
    padding: 10,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    zIndex: 10000,
    marginBottom: 0,
    width: "100%",
  },
  inputGroup: {
    flex: 1,
    minWidth: deviceInfo.isWebOrIPad ? "48%" : "100%",
    marginHorizontal: 4,
    zIndex: 10001,
  },
  datePickerContainer: {
    zIndex: 20020,
    position: "relative",
    overflow: "visible",
  },
  datePicker: {
    marginBottom: 10,
    position: "relative",
  },
  dropdownContainer: {
    marginBottom: 10,
  },
  dropdownStyle: {
    marginBottom: 10,
    marginHorizontal: deviceInfo.isWebOrIPad ? 4 : 0,
    zIndex: 20010,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
  },
  input: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 4,
    padding: deviceInfo.isWebOrIPad ? 10 : 8,
    marginBottom: 10,
    fontSize: 14,
  },
  inputError: {
    borderColor: "#dc3545",
    borderWidth: 1,
  },
  errorText: {
    color: "#dc3545",
    fontSize: 12,
    marginBottom: 10,
    marginLeft: deviceInfo.isWebOrIPad ? 4 : 0,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 20,
    marginLeft: deviceInfo.isIPad ? "75%" : 0,
    width: deviceInfo.isIPad ? "25%" : "100%",
  },
  cancelButton: {
    backgroundColor: "#ffff",
    padding: 0,
    borderRadius: 24,
    alignItems: "center",
    borderWidth: 0,
    marginRight:4,
    minWidth: deviceInfo.isWebOrIPad ? 120 : 100,
    height: deviceInfo.isWebOrIPad ? 32 : undefined,
  },
  cancelButtonText: {
    color: "#005CBC",
    fontWeight: "700",
    fontFamily: FontFamily.regular,
    fontSize: deviceInfo.isWebOrIPad ? 14 : 14,
  },
  addButton: {
    backgroundColor: "#0066CC",
    padding: 0,
    borderRadius: 24,
    alignItems: "center",
    minWidth: deviceInfo.isWebOrIPad ? 150 : 100,
    height: deviceInfo.isWebOrIPad ? 32 : undefined,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: deviceInfo.isWebOrIPad ? 14 : 14,
  },
  mobileButton: {
    flex: 1,
    minWidth: 0,
    
  },
  mobileAddButton: {
    width: "100%",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
  errorMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
  },
  errorMessageText: {
    color: "#721c24",
    fontSize: 16,
    flex: 1,
  },
  gpidStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: deviceInfo.isWebOrIPad ? 12 : 0,
    marginBottom: 16,
  },
  gpidStatusText: {
    color: "#0066cc",
    fontSize: 14,
    fontStyle: "italic",
  },
  gpidInfoText: {
    color: "#856404",
    fontSize: 14,
    fontStyle: "italic",
  },
  gpidIndicator: {
    color: "#28a745",
    fontSize: 12,
    fontStyle: "italic",
    fontWeight: "normal",
  },
  inputDisabled: {
    backgroundColor: "#f8f9fa",
    color: "#6c757d",
  },
  gpidInputContainer: {
    position: "relative",
  },
  gpidLoadingIndicator: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: [{ translateY: -10 }],
  },
  gpidLoadingText: {
    fontSize: 12,
    color: "#007bff",
    fontStyle: "italic",
  },
});

export default EmployeeFormSection;
